import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.jsx';
import { BarChart3, Users } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const TYPE_LABEL = { single: 'Escolha única', multiple: 'Múltipla escolha', open: 'Aberta' };

const ModeToggle = ({ mode, setMode, idx }) => (
  <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
    {[['bar', 'Barras'], ['pie', 'Pizza']].map(([m, l]) => (
      <button key={m} onClick={() => setMode(m)} data-testid={`${m}-${idx}`}
        className={`text-xs px-3 py-1 rounded transition-colors ${mode === m ? 'bg-white shadow-sm font-semibold' : 'text-slate-500'}`}>{l}</button>
    ))}
  </div>
);

const ChoiceChart = ({ q, mode }) => (
  <div className="h-72">
    <ResponsiveContainer width="100%" height="100%">
      {mode === 'bar' ? (
        <BarChart data={q.distribution} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
          <XAxis dataKey="option" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip formatter={(v, _n, p) => [`${v} (${p.payload.percent}%)`, 'Respostas']} />
          <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
        </BarChart>
      ) : (
        <PieChart>
          <Pie data={q.distribution} dataKey="count" nameKey="option" cx="50%" cy="50%" outerRadius={100} label={(e) => `${e.percent}%`}>
            {(q.distribution || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v, _n, p) => [`${v} (${p.payload.percent}%)`, p.payload.option]} />
          <Legend />
        </PieChart>
      )}
    </ResponsiveContainer>
  </div>
);

const OpenAnswers = ({ q, isAdmin }) => (
  <div className="space-y-2 max-h-80 overflow-y-auto">
    {(q.answers_open || []).length === 0 ? (
      <p className="text-sm text-slate-400">Nenhuma resposta ainda.</p>
    ) : q.answers_open.map((a, i) => (
      <div key={i} className="bg-slate-50 border-l-2 border-blue-400 px-3 py-2 rounded">
        {isAdmin && a.company_name && <p className="text-[11px] text-slate-500 font-semibold mb-0.5">{a.company_name}</p>}
        <p className="text-sm text-slate-700">{a.answer}</p>
      </div>
    ))}
  </div>
);

const QuestionResult = ({ q, idx, isAdmin }) => {
  const [mode, setMode] = useState('bar');
  return (
    <div className="border-b pb-5 last:border-b-0 last:pb-0" data-testid={`q-result-${idx}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <div>
          <h4 className="font-semibold text-slate-900">{idx + 1}. {q.text}</h4>
          <p className="text-xs text-slate-500 mt-0.5">Tipo: {TYPE_LABEL[q.type]} · {q.answered || 0} respostas</p>
        </div>
        {q.type !== 'open' && <ModeToggle mode={mode} setMode={setMode} idx={idx} />}
      </div>
      {q.type === 'open' ? <OpenAnswers q={q} isAdmin={isAdmin} /> : <ChoiceChart q={q} mode={mode} />}
      {q.type !== 'open' && (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {(q.distribution || []).map((d, i) => (
            <div key={i} className="bg-slate-50 rounded px-2 py-1"><span className="font-semibold">{d.option}:</span> {d.count} ({d.percent}%)</div>
          ))}
        </div>
      )}
    </div>
  );
};

const CompaniesTable = ({ rows }) => (
  <Card>
    <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4 text-blue-600" /> Respostas por Empresa</CardTitle></CardHeader>
    <CardContent>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2">Empresa</th>
              <th className="text-left px-3 py-2">Última atualização</th>
              <th className="text-left px-3 py-2">Canal</th>
              <th className="text-left px-3 py-2">Respostas</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.company_id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} data-testid={`company-row-${r.company_id}`}>
                <td className="px-3 py-2 font-medium text-slate-800">{r.company_name}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{new Date(r.updated_at || r.submitted_at).toLocaleString('pt-BR')}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.via === 'link' ? 'Link' : 'Login'}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.answers?.length || 0} itens</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

export const DiagnosticResultsView = ({ data, isAdmin }) => (
  <div className="flex flex-col gap-5" data-testid="results-view">
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-xl p-4 text-white">
        <p className="text-[11px] opacity-60 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Total de Respostas</p>
        <p className="text-2xl font-bold mt-1" data-testid="results-total">{data.total_responses}</p>
      </div>
      <div className="bg-blue-50 rounded-xl p-4">
        <p className="text-[11px] text-blue-600">Perguntas</p>
        <p className="text-2xl font-bold text-blue-700">{data.questions?.length || 0}</p>
      </div>
    </div>
    <Card>
      <CardHeader><CardTitle className="text-base">Tabulação por Pergunta</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        {(data.questions || []).map((q, idx) => <QuestionResult key={q.id} q={q} idx={idx} isAdmin={isAdmin} />)}
      </CardContent>
    </Card>
    {isAdmin && (data.responses_by_company || []).length > 0 && <CompaniesTable rows={data.responses_by_company} />}
  </div>
);

export default DiagnosticResultsView;
